import { useState, useEffect } from "react";
import { Navigate } from "react-router";
import { toast } from "sonner";
import { useTranslation } from "@/i18n/use-translation";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/stores/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { authErrorMessage } from "@/lib/auth-error";

export default function MfaChallengePage() {
  const { t } = useTranslation();
  const session = useAuthStore((s) => s.session);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [codeSent, setCodeSent] = useState(false);
  const [aalLevel, setAalLevel] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    supabase.auth.mfa.getAuthenticatorAssuranceLevel().then(({ data }) => {
      setAalLevel(data?.currentLevel ?? null);
      setChecking(false);
    });
  }, [session]);

  // No session at all → go to login
  if (!session) {
    return <Navigate to="/login" replace />;
  }

  // Still checking AAL
  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
      </div>
    );
  }

  const phoneFactor = session.user?.factors?.find(
    (f) => f.factor_type === "phone" && f.status === "verified",
  );

  // Already AAL2 or no phone factor enrolled → go to app
  if (aalLevel === "aal2" || !phoneFactor) {
    return <Navigate to="/" replace />;
  }

  async function handleSendCode() {
    if (!phoneFactor) return;

    const { data, error } = await supabase.auth.mfa.challenge({
      factorId: phoneFactor.id,
      channel: "sms",
    });

    if (error) {
      toast.error(authErrorMessage(t, error));
    } else {
      setChallengeId(data.id);
      setCodeSent(true);
      setCode("");
      toast.success(t("auth.mfaCodeSent"));
    }
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    if (!phoneFactor || !challengeId) return;

    setLoading(true);
    try {
      const { error } = await supabase.auth.mfa.verify({
        factorId: phoneFactor.id,
        challengeId,
        code: code.trim(),
      });

      if (error) {
        toast.error(authErrorMessage(t, error));
        setLoading(false);
        return;
      }

      // Session is now AAL2 — auth state change will handle redirect
    } catch {
      toast.error(t("auth.mfaFailed"));
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="text-lg font-medium">{t("auth.mfaTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">{t("auth.mfaDescription")}</p>
          {!codeSent ? (
            <Button type="button" onClick={handleSendCode}>
              {t("auth.mfaSendCode")}
            </Button>
          ) : (
            <form onSubmit={handleVerify} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="mfa-code" className="text-sm font-medium">
                  {t("auth.mfaCode")}
                </label>
                <Input
                  id="mfa-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" disabled={loading || code.length < 6}>
                {loading ? t("auth.mfaVerifying") : t("auth.mfaVerify")}
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={handleSendCode}>
                {t("auth.mfaResend")}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
